import React, { useEffect, useRef, useState } from 'react';
import { InputNumber } from 'primereact/inputnumber';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { Card } from 'primereact/card';
import api from '../../services/api';

const FIELDS = [
  { key: 'baseReport', label: 'Base report' },
  { key: 'withText', label: 'With open text' },
  { key: 'withPhoto', label: 'With photo' },
  { key: 'withLineStart', label: 'With line start location' },
  { key: 'withWaitBucket', label: 'With wait-to-cross estimate' },
  { key: 'withWaitingFromPosition', label: 'With waiting-from-position' },
  { key: 'withLocation', label: 'With current location' },
  { key: 'withReaction', label: 'Per reaction received' },
];

export default function ReportPointsConfigManager() {
  const toast = useRef(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({});

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/report_points_config');
        setConfig(data);
      } catch (err) {
        toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to load' });
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const save = async () => {
    try {
      setSaving(true);
      const payload = {};
      FIELDS.forEach(({ key }) => {
        payload[key] = Number(config[key]) || 0;
      });
      const { data } = await api.put('/report_points_config', payload);
      setConfig(data);
      toast.current?.show({ severity: 'success', summary: 'Saved', detail: 'Points config updated' });
    } catch (err) {
      toast.current?.show({ severity: 'error', summary: 'Error', detail: 'Failed to save' });
    } finally {
      setSaving(false);
    }
  };

  const previewTotal = FIELDS.filter((f) => f.key !== 'withReaction').reduce(
    (sum, f) => sum + (Number(config[f.key]) || 0),
    0
  );

  return (
    <Card>
      <div className="card p-4">
        <Toast ref={toast} />
        <div className="grid mb-4">
          <div className="col-6 flex align-items-center">
            <h2 className="m-0 main-text font-bold vertical-align-middle">
              <span>Report points</span>
            </h2>
          </div>
          <div className="col-6 flex justify-content-end">
            <Button
              label="Save"
              icon="pi pi-check"
              className="p-button-primary p-button-sm secondary-button"
              loading={saving}
              disabled={loading}
              onClick={save}
            />
          </div>
        </div>

        <p className="admin-page-hint mb-4">
          Points awarded when a mobile user submits a report (shown immediately in the app).
        </p>

        {loading ? (
          <div
            className="flex flex-column align-items-center justify-content-center"
            style={{ minHeight: '300px' }}
          >
            <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem', color: '#2020A0' }} />
            <div style={{ marginTop: '1rem', fontSize: '1.2rem', color: '#555' }}>
              Loading points config...
            </div>
          </div>
        ) : (
          <>
            <div className="grid">
              {FIELDS.map(({ key, label }) => (
                <div key={key} className="col-12 md:col-6 lg:col-4 p-fluid mb-3">
                  <label style={{ fontSize: '0.95rem' }}>
                    <strong>{label}</strong>
                  </label>
                  <InputNumber
                    value={config[key] ?? 0}
                    onValueChange={(e) => setConfig({ ...config, [key]: e.value ?? 0 })}
                    min={0}
                    showButtons
                    className="w-full"
                  />
                </div>
              ))}
            </div>

            <div
              className="mt-2 p-3 border-round"
              style={{ background: '#f8fafc', border: '1px solid #e5e7eb' }}
            >
              <strong className="main-text">Max if all report elements included:</strong>{' '}
              {previewTotal} pts
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
